import { GoogleMap, GoogleMapProps } from '@react-google-maps/api'
import debounce from 'lodash.debounce'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import * as InteractionStudioService from 'api/interactionStudio'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { EnsureGoogleMapsLoaded, useGoogleMapsContext } from 'contexts/GoogleMapsContext'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import usePrevious from 'hooks/usePrevious'
import useResponsiveSelector from 'hooks/useResponsiveSelector'
import getOfferListKey from 'lib/offerListKey'
import PropertyMarker from './PropertyMarker'
import useSearchResult from './useSearchMapResults'
import {
  centerAndZoomOnMarker,
  fitMarkersOnMap,
  getMarkersByOffers,
  getPadding,
  isMarkerWithinBounds,
  mapHasAnyMarkersInView,
  parseBounds,
  subtractPaddingFromBounds,
  useVisitedMarkersState,
} from './utils'

import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import MarkerRenderer from 'components/Pages/HotelSearchPage/AdvancedMarker/MarkerRenderer'
import config from 'constants/config'
import { HOTEL_SEARCH_TYPES } from 'constants/offer'
import { queryKeyMapPinCount } from 'constants/url'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import useOffers from 'hooks/Offers/useOffers'
import { useAppSelector } from 'hooks/reduxHooks'
import { OptimizelyClientSideExperiments, useOptimizelyExperiment } from 'lib/optimizely'
import { getQueryParam } from 'lib/urlUtils'
import { connect } from 'react-redux'
import { GlobalSearchStateActions } from '../../../contexts/GlobalSearch/GlobalSearchState'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: center;
`

const LoadingModalContent = styled.div`
  text-align: center;
`
const ColourfulLoadingAnimation = styled(LoadingAnimation)`
  color: ${props => props.theme.palette.primary.normal};
`

interface MappedStateProps {
  search: string;
}

interface Props {
  filters: App.OfferListFilters;
  onBoundsChanged: (newBounds: google.maps.LatLngBounds) => void;
  onSelectOffers: (offers: Array<App.Offer | App.BedbankOffer>) => void;
  onDeselectOffers: () => void;
  onError: () => void;
  onZeroResults: () => void;
  selectedOfferIds: Array<{
    kind: 'le' | 'bedbank',
    id: string,
  }>;
}

const MAP_OPTIONS: GoogleMapProps['options'] = {
  gestureHandling: 'greedy',
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  zoomControlOptions: {
    position: 9,
  },
  minZoom: 3,
  maxZoom: 19,
  mapId: config.GOOGLE_MAP_ID,
}

const MAP_STYLE = {
  height: '100%',
  width: '100%',
  flexGrow: 1,
}

const typeOrder = {
  hotel: 1,
  tactical_ao_hotel: 2,
  bedbank_hotel: 3,
}

function SearchResultsMap(props: Props & MappedStateProps) {
  const {
    filters,
    onBoundsChanged,
    onSelectOffers,
    onDeselectOffers,
    onError,
    onZeroResults,
    selectedOfferIds,
    search,
  } = props

  const {
    searchTargetLabel,
  } = useContext(GlobalSearchStateContext)

  const {
    isLoaded: isGoogleMapsLoaded,
  } = useGoogleMapsContext()

  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)

  const priceMarkersExperimentEnabled = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.searchPriceMapMarkers)

  const shouldShowMapPinPrices = (config.ENABLE_MAP_SEARCH_PRICE_PINS && priceMarkersExperimentEnabled)
  const mapPinCount = Number(getQueryParam(search, queryKeyMapPinCount)) || undefined

  const setSearchLabel = useCallback((searchLabel: string) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_SEARCH_TARGET_LABEL, searchTargetLabel: searchLabel })
  }, [globalSearchDispatch])

  const searchFilters = useMemo((): App.OfferListFilters => ({
    ...filters,
    limit: mapPinCount ?? config.search.mapView.pinLimit + config.search.mapView.additionalPins,
    offerTypes: [
      ...HOTEL_SEARCH_TYPES,
      'bedbank_hotel',
    ],
    showUnavailableResult: false,
  }), [filters, mapPinCount])

  const currentSearchResult = useSearchResult(searchFilters)

  const { allOffersLoaded } = currentSearchResult
  const currentOfferListKey = getOfferListKey(searchFilters)

  const searchSessionId = useAppSelector(state => state.analytics.search.sessions[currentOfferListKey])

  const getTrackingDataFromMarker = useCallback((marker: App.SearchMapMarker, pinType: string): InteractionStudioService.TrackMapPinClickParams => {
    const offer = marker.offers[0]
    return {
      offerId: offer.id,
      offerType: offer.type,
      pinType,
      price: undefined,
      listId: searchSessionId,
      index: 0,
    }
  }, [searchSessionId])

  const offerIds = useMemo(() => selectedOfferIds.map(id => id.id), [selectedOfferIds])
  const [selectedOffers] = useOffers<App.Offer | App.BedbankOffer>(offerIds)

  const [map, setMap] = useState<google.maps.Map | null>(null)
  const containerRef = useRef<HTMLDivElement>()

  /*
    Normally when the map goes idle, it means the user has manually panned/zoomed/rotated,
    and we should do a new bounds search.
    But it also happens when the map loads, or when the map bounds are moved programmatically,
    or when the map size changes due to resizing the window or dismissing a notification.
    We can distinguish between these scenarios with a combination of:
    - Tracking whether the map has been panned/zoomed/rotated, via `hasInteracted` ref
    - Explicitly ignoring the next `onIdle` event, via `ignoreNextIdle` ref
    Neither method on its own is quite enough to handle all edge cases.
    Ideally the map would have a built-in callback for this, but it doesn't.
  */
  const ignoreNextIdle = useRef<boolean>(true)
  const hasInteracted = useRef<boolean>(false)
  const onInteract = useCallback(() => { hasInteracted.current = true }, [])

  // Determine whether the initial offers have loaded
  const initialOffersLoaded = useRef(false)
  const isFirstLoad = allOffersLoaded && !initialOffersLoaded.current
  if (allOffersLoaded) { initialOffersLoaded.current = true }

  // Check if this is a new destination or property search, in which case we need to update the map position
  const prevAllOffersLoaded = usePrevious(allOffersLoaded, false)
  const prevFilters = usePrevious(filters, {})
  const prevOfferListKey = usePrevious(currentOfferListKey)
  const isNewlyLoaded = allOffersLoaded && (currentOfferListKey !== prevOfferListKey || !prevAllOffersLoaded)
  const isNewDestination = !!filters.destinationId && filters.destinationId !== prevFilters.destinationId
  const isNewProperty = !!filters.propertyId && filters.propertyId !== prevFilters.propertyId
  const hasDates = !!(filters.checkIn && filters.checkOut)
  const isNewDates = hasDates && (filters.checkIn !== prevFilters.checkIn || filters.checkOut !== prevFilters.checkOut)
  const isNewRooms = JSON.stringify(filters.rooms) !== JSON.stringify(prevFilters.rooms)
  const isNewBounds = !!filters.bounds && filters.bounds !== prevFilters.bounds
  const isNewHolidayTypes = JSON.stringify(filters.holidayTypes) !== JSON.stringify(prevFilters.holidayTypes)
  const isNewAmenities = JSON.stringify(filters.amenities) !== JSON.stringify(prevFilters.amenities)
  const isNewManualSearch = isNewDestination || isNewProperty || isNewDates || isNewRooms || isNewHolidayTypes || isNewAmenities
  const isNewSearch = isNewManualSearch || isNewBounds

  const lastSearch = useRef({ destination: false, property: false, bounds: false, dates: false, rooms: false })
  if (isNewSearch) {
    lastSearch.current = {
      destination: isNewDestination,
      property: isNewProperty,
      bounds: isNewBounds,
      dates: isNewDates,
      rooms: isNewRooms,
    }
  }

  const shouldClearPreviousResults = isNewManualSearch || !!currentSearchResult.error

  const showLoadingModal = useRef<boolean>(false)
  if (isNewDestination && initialOffersLoaded.current && !allOffersLoaded) {
    showLoadingModal.current = true
  } else if (allOffersLoaded) {
    showLoadingModal.current = false
  }

  // When the filters have changed, we should continue showing the offers from the last few offer lists
  const recentSearchResults = useRef<Array<Array<App.Offer | App.BedbankOffer>>>([])
  if (shouldClearPreviousResults) {
    recentSearchResults.current = []
  }
  if (isNewlyLoaded) {
    recentSearchResults.current = [currentSearchResult.offers]
  }

  useEffect(() => {
    if (currentSearchResult.error) {
      onError()
    }
  }, [currentSearchResult.error, onError])

  const recent = recentSearchResults.current

  // Combine offers from the recent results and currently selected offers
  const currentOffers = useMemo(() => {
    const [_kind, id] = filters.propertyId?.split(':') || []
    const allRecentResults = recent.flat().filter(offer => !('isSoldOut' in offer) || offer.isSoldOut === false || (id && offer.property?.id === id))
    const allRecentResultIds = new Set(allRecentResults.map(r => r.id))
    const combinedResults = allRecentResults.concat(selectedOffers.filter(offer => !allRecentResultIds.has(offer.id)))
    return combinedResults.sort((a, b) => {
      const typeA = a.type || ''
      const typeB = b.type || ''

      return typeOrder[typeA] - typeOrder[typeB]
    })
  }, [recent, selectedOffers, filters.propertyId])

  const currentBounds = isGoogleMapsLoaded && filters?.bounds !== undefined && parseBounds(filters.bounds)

  const markers = useMemo(() => {
    const offerMarkers = getMarkersByOffers(currentOffers)
    return currentBounds ? offerMarkers.filter((marker) => isMarkerWithinBounds(marker, currentBounds)) : offerMarkers
  }, [currentBounds, currentOffers])

  const activeMarkerId = selectedOfferIds.map(idWithKind => idWithKind.id).join(',')
  const [visitedMarkerIds, addVisitedMarkerId] = useVisitedMarkersState()

  useEffect(() => {
    if (activeMarkerId) {
      addVisitedMarkerId(activeMarkerId)
    }
  }, [activeMarkerId, addVisitedMarkerId])

  const onMarkerClick = useCallback((marker: App.SearchMapMarker, pinType: string) => {
    onSelectOffers(marker.offers)
    InteractionStudioService.trackMapPinClick(getTrackingDataFromMarker(marker, pinType))
  }, [getTrackingDataFromMarker, onSelectOffers])

  const currentOffersBestPrices = useOffersBestPrices(currentOffers, filters)

  // Determine whether to report zero results
  const hadMarkersLoadedOrInViewAtLastLoad = useRef(false)
  useEffect(() => {
    if (isNewlyLoaded) {
      const offersLoadedOrInView = currentSearchResult.totalNumOffers > 0 || mapHasAnyMarkersInView(map, markers)
      if (
        !offersLoadedOrInView &&
        !currentSearchResult.error &&
        hadMarkersLoadedOrInViewAtLastLoad.current
      ) {
        onZeroResults()
      }
      hadMarkersLoadedOrInViewAtLastLoad.current = offersLoadedOrInView
    }
  }, [currentSearchResult, isNewlyLoaded, map, markers, onZeroResults])

  // If user searched for a property, select it. If user searched for a destination, deselect offers
  useEffect(() => {
    if (isNewlyLoaded && !isFirstLoad) {
      if (!filters.destinationId && searchTargetLabel !== 'Map area') {
        setSearchLabel('Map area')
      }
      if (lastSearch.current.property) {
        const [_kind, id] = filters.propertyId.split(':')
        const marker = markers.find(marker => marker.offers.some(offer => offer.property.id === id))
        onMarkerClick(marker, 'pin')
      }
      else if (lastSearch.current.destination && selectedOfferIds.length > 0) {
        onDeselectOffers()
      }
    }
  }, [isNewlyLoaded, isFirstLoad, markers, selectedOfferIds.length, filters.propertyId, onMarkerClick, onDeselectOffers, filters.destinationId, setSearchLabel, searchTargetLabel])

  // Fit the correct bounds on load
  const onLoad = useCallback((map: google.maps.Map) => {
    ignoreNextIdle.current = true
    if (filters.propertyId) {
      const [_kind, id] = filters.propertyId.split(':')
      const marker = markers.find(marker => marker.offers.some(offer => offer.property.id === id))
      if (marker) {
        centerAndZoomOnMarker(map, marker, true) } else {
        fitMarkersOnMap(map, markers)
      }
    }
    else if (filters.bounds) {
      map.fitBounds(currentBounds, getPadding(true))
    }
    else {
      if (selectedOfferIds.length > 0 && markers.length > 0) {
        const selectedMarker = markers.find((marker) => marker.id.split(',').includes(selectedOfferIds[0].id))
        if (selectedMarker) {
          fitMarkersOnMap(map, markers, selectedMarker)
        }
      } else {
        fitMarkersOnMap(map, markers)
      }
    }
    setMap(map)
  }, [currentBounds, filters.bounds, filters.propertyId, markers, selectedOfferIds])

  // Update the bounds if we've just finished loading offers for a fresh destination or property search
  useEffect(() => {
    if (isNewlyLoaded && (lastSearch.current.destination || lastSearch.current.property)) {
      ignoreNextIdle.current = true

      if (lastSearch.current.property) {
        const [_kind, id] = filters.propertyId.split(':')
        const marker = markers.find(marker => marker.offers.some(offer => offer.property.id === id))
        if (marker) {
          centerAndZoomOnMarker(map, marker, true)
          return
        }
      }

      fitMarkersOnMap(map, markers)
    } }, [filters.propertyId, isNewlyLoaded, map, markers])

  const onUnmount = useCallback(() => {
    setMap(null)
  }, [])

  // Maintain mapping of `google.maps.Marker` instances to the source `App.SearchMapMarker` objects
  // so that the clusterer calculator can figure out what offers are in a cluster
  const markerMap = useRef(new Map<google.maps.Marker, App.SearchMapMarker>())
  const onMarkerLoad = useCallback((markerInstance: google.maps.Marker, markerData: App.SearchMapMarker) => {
    markerMap.current.set(markerInstance, markerData)
  }, [])
  const onMarkerUnmount = useCallback((markerInstance: google.maps.Marker) => {
    markerMap.current.delete(markerInstance)
  }, [])

  const onUserMovedMap = useMemo<() => void>(() => debounce(
    () => {
      const boundsWithoutMargin = subtractPaddingFromBounds(map.getBounds(), containerRef.current.getBoundingClientRect())
      onBoundsChanged(boundsWithoutMargin)
    },
    2000,
    { leading: true, trailing: true },
  ), [map, onBoundsChanged])

  const onIdle = useCallback(() => {
    if (!ignoreNextIdle.current && hasInteracted.current) {
      onUserMovedMap()
    }
    ignoreNextIdle.current = false
    hasInteracted.current = false
  }, [onUserMovedMap])

  const isMobileSize = useResponsiveSelector({
    xxs: true,
    xs: true,
    sm: false,
    md: false,
    lg: false,
    xl: false,
  })
  const mapOptions = useMemo(() => ({
    ...MAP_OPTIONS,
    zoomControl: !isMobileSize,
  }), [isMobileSize])

  return <Container ref={containerRef}>
    <EnsureGoogleMapsLoaded loadingElement={<LoadingIndicator inline />}>
      {!initialOffersLoaded.current && <LoadingIndicator inline />}

      {initialOffersLoaded.current && <GoogleMap
        id="le-map"
        zoom={17}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={onDeselectOffers}
        onDrag={onInteract}
        onZoomChanged={onInteract}
        onHeadingChanged={onInteract}
        onIdle={onIdle}
        mapContainerStyle={MAP_STYLE}
        options={mapOptions}
      >
        {shouldShowMapPinPrices &&
          <MarkerRenderer
            markers={markers}
            map={map}
            onMarkerClick={onMarkerClick}
            bestPrices={currentOffersBestPrices}
            activeMarker={activeMarkerId}
            visitedMarkers={visitedMarkerIds}
          />
         }
        {!shouldShowMapPinPrices &&
          markers.map((marker, index) => (
            <PropertyMarker
              key={marker.id}
              marker={marker}
              active={marker.id === activeMarkerId}
              soldOut={marker.offers.every(offer => currentOffersBestPrices[offer.id]?.soldOut)}
              visited={visitedMarkerIds.has(marker.id)}
              onClick={onMarkerClick}
              onLoad={onMarkerLoad}
              onUnmount={onMarkerUnmount}
              scaleDown={isMobileSize}
              isPill={index + 1 > config.search.mapView.pinLimit}
            >
            </PropertyMarker>
          ))
        }
      </GoogleMap>}
    </EnsureGoogleMapsLoaded>

    <ModalBase isOpen={showLoadingModal.current}>
      <ModalBody>
        <ModalContent>
          <LoadingModalContent>
            <ColourfulLoadingAnimation/>
            <BodyText variant="large" weight="bold">One moment while we find you the best hotel offers in this area</BodyText>
          </LoadingModalContent>
        </ModalContent>
      </ModalBody>
    </ModalBase>
  </Container>
}

export default connect<MappedStateProps, undefined, Props, App.State>((state): MappedStateProps => {
  return {
    search: state.router.location.search,
  }
})(SearchResultsMap)
